import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Button,
  Spinner,
  Alert,
  Table,
} from "reactstrap";
import {
  FaFileAlt,
  FaFilter,
  FaFileExcel,
  FaChartBar,
  FaQuestionCircle,
  FaStar,
} from "react-icons/fa";
import { localesAPI } from "../utils/api";

const Reportes = () => {
  // Fecha de hoy en formato YYYY-MM-DD
  const obtenerFechaHoy = () => {
    const hoy = new Date();
    const dia = String(hoy.getDate()).padStart(2, '0');
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const anio = hoy.getFullYear();
    return `${anio}-${mes}-${dia}`;
  };

  // Mismo día pero del mes anterior
  const obtenerFechaHaceUnMes = () => {
    const hoy = new Date();
    const hace1mes = new Date(hoy.getFullYear(), hoy.getMonth() - 1, hoy.getDate());
    const dia = String(hace1mes.getDate()).padStart(2, '0');
    const mes = String(hace1mes.getMonth() + 1).padStart(2, '0');
    const anio = hace1mes.getFullYear();
    return `${anio}-${mes}-${dia}`;
  };

  const fechaHoy = obtenerFechaHoy();
  const fechaHaceUnMes = obtenerFechaHaceUnMes();

  /* ────────────────────────────────────────────────────────────
     Estados
  ──────────────────────────────────────────────────────────── */
  const [filterTipo, setFilterTipo] = useState("all");
  const [fechaInicio, setFechaInicio] = useState(fechaHaceUnMes);
  const [fechaFin, setFechaFin] = useState(fechaHoy); // Inicializa al día de hoy

  // Pestaña activa
  const [tabActiva, setTabActiva] = useState("comentarios");

  // Resultados
  const [reportePorComentarios, setReportePorComentarios] = useState([]);
  const [reportePorPreguntas, setReportePorPreguntas] = useState([]);
  const [loadingReporte, setLoadingReporte] = useState(false);
  const [errorReporte, setErrorReporte] = useState(null);
  const [reporteGenerado, setReporteGenerado] = useState(false);

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Auto-generar reporte al montar el componente
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { handleGenerar(); }, []);

  // Inyectar estilos modernos específicos
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "reportes-styles-eval";
    style.textContent = `
      .modern-select {
        appearance: none !important;
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e") !important;
        background-position: right 12px center !important;
        background-repeat: no-repeat !important;
        background-size: 16px !important;
        padding-right: 40px !important;
      }
      .modern-select:hover {
        border-color: #5A0C62 !important;
        box-shadow: 0 4px 12px rgba(90, 12, 98, 0.15) !important;
      }
      .modern-select:focus {
        border-color: #DC017F !important;
        box-shadow: 0 0 0 3px rgba(220, 1, 127, 0.1) !important;
      }
      .reporte-tab-btn {
        transition: all 0.25s ease;
        border-radius: 12px !important;
        font-weight: 700 !important;
        font-size: 13px !important;
        padding: 10px 20px !important;
        border: none !important;
      }
      .reporte-tab-btn.active-tab {
        background: linear-gradient(135deg, #5A0C62 0%, #DC017F 100%) !important;
        color: #fff !important;
        box-shadow: 0 4px 14px rgba(90,12,98,0.3) !important;
      }
      .reporte-tab-btn.inactive-tab {
        background: #f8f9fa !important;
        color: #6c757d !important;
        border: 1px solid #dee2e6 !important;
      }
      .reporte-tab-btn.inactive-tab:hover {
        background: #e9ecef !important;
        color: #5A0C62 !important;
      }
      .btn-excel-eval {
        background: linear-gradient(135deg, #217346 0%, #1fa463 100%) !important;
        border: none !important;
        border-radius: 12px !important;
        font-weight: 700 !important;
        color: #fff !important;
        box-shadow: 0 3px 10px rgba(33,115,70,0.3) !important;
        transition: all 0.25s ease !important;
        display: inline-flex !important;
        align-items: center !important;
        gap: 6px !important;
      }
      .btn-excel-eval:hover {
        transform: translateY(-1px) !important;
        box-shadow: 0 6px 16px rgba(33,115,70,0.4) !important;
        color: #fff !important;
      }
      .stars-cell-eval {
        display: inline-flex;
        align-items: center;
        gap: 2px;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("reportes-styles-eval");
      if (el) document.head.removeChild(el);
    };
  }, []);

  /* ────────────────────────────────────────────────────────────
     Generar reporte
  ──────────────────────────────────────────────────────────── */
  const handleGenerar = async () => {
    setErrorReporte(null);
    setLoadingReporte(true);
    setReporteGenerado(false);
    setCurrentPage(1); // Resetear a la primera página al generar nuevo reporte

    try {
      const params = new URLSearchParams();
      if (filterTipo !== "all") params.append("tipo", filterTipo);
      if (fechaInicio) params.append("fechaDesde", fechaInicio);
      if (fechaFin) params.append("fechaHasta", fechaFin);

      // Reporte por Comentarios: detalle por evaluación
      const resGeneral = await localesAPI.getReporteComentarios(params.toString());
      setReportePorComentarios(resGeneral.data || []);

      // Reporte por Preguntas: detalle por respuestas
      const resPreguntas = await localesAPI.getReportePreguntas(params.toString());
      setReportePorPreguntas(resPreguntas.data || []);

      setReporteGenerado(true);
    } catch (err) {
      setErrorReporte("Error al generar los reportes. Verifica la conexión y vuelve a intentarlo.");
    } finally {
      setLoadingReporte(false);
    }
  };

  /* ────────────────────────────────────────────────────────────
     Limpiar
  ──────────────────────────────────────────────────────────── */
  const handleLimpiar = () => {
    setFilterTipo("all");
    setFechaInicio(fechaHaceUnMes);
    setFechaFin(fechaHoy);
    setReportePorComentarios([]);
    setReportePorPreguntas([]);
    setReporteGenerado(false);
    setErrorReporte(null);
  };

  /* ────────────────────────────────────────────────────────────
     Exportar a Excel (CSV)
  ──────────────────────────────────────────────────────────── */
  const exportarExcel = (tipo) => {
    let headers = [];
    let rows = [];

    if (tipo === "comentarios") {
      headers = ["ID Evaluacion", "Tipo", "Turno", "ID Local", "Local", "Puntuacion", "Comentario", "Fecha"];
      rows = reportePorComentarios.map((r, i) => [
        r.evaluacion ?? "",
        r.tipo_local || "",
        r.turno ?? "",
        r.local_id ?? "",
        r.local_nombre || "",
        r.puntuacion ?? "",
        (r.comentario || "").replace(/"/g, "'"),
        r.fecha || "",
      ]);
    } else {
      headers = ["ID Evaluacion", "Tipo", "Turno", "ID Local", "Local", "ID Pregunta", "Pregunta", "Puntuacion", "Fecha"];
      rows = reportePorPreguntas.map((r) => [
        r.evaluacion ?? "",
        r.tipo_local || "",
        r.turno ?? "",
        r.local_id ?? "",
        r.local_nombre || "",
        r.pregunta_id ?? "",
        r.pregunta || "",
        r.puntuacion ?? "",
        r.fecha || "",
      ]);
    }

    const csvContent =
      "\uFEFF" +
      [headers.join(","), ...rows.map((r) => r.map((c) => `"${c}"`).join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reporte_${tipo}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /* ────────────────────────────────────────────────────────────
     Helpers visuales
  ──────────────────────────────────────────────────────────── */
  const renderStars = (score) => {
    const num = parseFloat(score) || 0;
    return (
      <span className="stars-cell-eval">
        {[1, 2, 3, 4, 5].map((i) => (
          <FaStar
            key={i}
            size={13}
            style={{ color: i <= Math.round(num) ? "#FFD700" : "#dee2e6" }}
          />
        ))}
        <span style={{ marginLeft: 5, fontSize: 12, color: "#6c757d", fontWeight: 600 }}>
          {num.toFixed(1)}
        </span>
      </span>
    );
  };



  /* ────────────────────────────────────────────────────────────
     Render
  ──────────────────────────────────────────────────────────── */
  return (
    <>
      {/* Cabecera degradada */}
      <div
        className="header pb-8 pt-5 pt-md-8"
        style={{ background: "linear-gradient(135deg, #5A0C62 0%, #DC017F 100%)" }}
      >
        <Container fluid>
          <div className="header-body">
            <Row>
              <Col>
                <h6 className="h2 text-white d-inline-block mb-0">Reportes de Evaluaciones</h6>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      {/* Contenedor principal con margen negativo */}
      <Container className="mt--8" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              {/* Encabezado del Card */}
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <div className="col">
                    <h3 className="mb-0">Reportes</h3>
                  </div>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Filtros alineados igual que en Evaluaciones */}
                <Row className="mb-4 g-3">

                  {/* Tipo de Local */}
                  <Col xs="12" sm="6" md="3">
                    <FormGroup className="mb-0">
                      <div className="custom-select-wrapper">
                        <Input
                          type="select"
                          value={filterTipo}
                          onChange={(e) => setFilterTipo(e.target.value)}
                          className="form-control-alternative modern-select"
                          style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            border: '2px solid #e9ecef',
                            borderRadius: '12px',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#495057',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            width: '100%'
                          }}
                        >
                          <option value="all">🏪 Todos los Tipos</option>
                          <option value="alimentos">🍽️ Alimentos</option>
                          <option value="miscelaneas">🛒 Misceláneas</option>
                          <option value="taxis">🚕 Taxis</option>
                          <option value="estacionamiento">🅿️ Estacionamiento</option>
                          <option value="sanitarios">🚾 Sanitarios</option>
                        </Input>
                      </div>
                    </FormGroup>
                  </Col>

                  {/* Fecha Inicio */}
                  <Col xs="6" sm="6" md="3">
                    <FormGroup className="mb-0">
                      <Input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        max={fechaFin || fechaHoy}
                        className="form-control-alternative"
                        style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#495057',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </FormGroup>
                  </Col>

                  {/* Fecha Fin */}
                  <Col xs="6" sm="6" md="3">
                    <FormGroup className="mb-0">
                      <Input
                        type="date"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        min={fechaInicio}
                        max={fechaHoy} // Topado a la fecha de hoy
                        className="form-control-alternative"
                        style={{
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          padding: '12px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#495057',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </FormGroup>
                  </Col>

                  {/* Botones */}
                  <Col xs="12" sm="12" md="3" className="d-flex align-items-center" style={{ gap: 8 }}>
                    <Button
                      color="primary"
                      onClick={handleGenerar}
                      disabled={loadingReporte}
                      className="d-flex align-items-center justify-content-center"
                    >
                      {loadingReporte ? (
                        <Spinner size="sm" className="mr-2">{null}</Spinner>
                      ) : (
                        <FaChartBar size={14} className="mr-2" />
                      )}
                      Generar
                    </Button>
                    <Button
                      color="secondary"
                      onClick={handleLimpiar}
                      disabled={loadingReporte}
                      className="d-flex align-items-center"
                      style={{
                        borderRadius: '12px',
                        padding: '10px 16px',
                        fontWeight: '700',
                        fontSize: '14px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <FaFilter className="mr-1" />
                      Limpiar
                    </Button>
                  </Col>
                </Row>

                {errorReporte && (
                  <Alert color="danger" className="mb-4">
                    {errorReporte}
                  </Alert>
                )}

                {/* Mostrar los resultados de los reportes generados a la par */}
                {reporteGenerado && (
                  <div className="mt-4">
                    <Row className="mb-3 align-items-center">
                      <Col xs="12" md="8">
                        <div className="d-flex" style={{ gap: 10 }}>
                          <button
                            className={`btn reporte-tab-btn ${tabActiva === "comentarios" ? "active-tab" : "inactive-tab"}`}
                            onClick={() => setTabActiva("comentarios")}
                          >
                            <FaFileAlt size={13} className="mr-2" />
                            Reporte por Comentarios
                          </button>
                          <button
                            className={`btn reporte-tab-btn ${tabActiva === "preguntas" ? "active-tab" : "inactive-tab"}`}
                            onClick={() => setTabActiva("preguntas")}
                          >
                            <FaQuestionCircle size={13} className="mr-2" />
                            Reporte por Preguntas
                          </button>
                        </div>
                      </Col>
                      <Col xs="12" md="4" className="d-flex justify-content-md-end mt-3 mt-md-0">
                        <Button
                          className="btn-excel-eval px-4 py-2"
                          onClick={() => exportarExcel(tabActiva)}
                        >
                          <FaFileExcel size={14} />
                          Exportar Excel
                        </Button>
                      </Col>
                    </Row>

                    {/* Lógica de paginación para Reporte por Comentarios */}
                    {(() => {
                      if (tabActiva !== "comentarios") return null;
                      if (reportePorComentarios.length === 0) {
                        return (
                          <div className="text-center py-5 text-muted">
                            No hay datos disponibles para el reporte por comentarios.
                          </div>
                        );
                      }

                      // Cálculos de paginación
                      const indexOfLastItem = currentPage * itemsPerPage;
                      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                      const currentItems = reportePorComentarios.slice(indexOfFirstItem, indexOfLastItem);
                      const totalPages = Math.ceil(reportePorComentarios.length / itemsPerPage);

                      return (
                        <>
                          <div className="table-responsive">
                            <Table className="align-items-center table-flush" hover>
                              <thead className="thead-light">
                                <tr>
                                  <th>ID</th>
                                  <th>Tipo</th>
                                  <th>Turno</th>
                                  <th>ID Local</th>
                                  <th>Local</th>
                                  <th>Puntuación</th>
                                  <th>Comentario</th>
                                  <th>Fecha</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentItems.map((row, idx) => (
                                  <tr key={row.evaluacion ?? idx}>
                                    <td style={{ color: "#adb5bd", fontSize: 11 }}>{row.evaluacion}</td>
                                    <td>{row.tipo_local || "—"}</td>
                                    <td style={{ textAlign: "center" }}>{row.turno ?? "—"}</td>
                                    <td style={{ color: "#adb5bd", fontSize: 11 }}>{row.local_id}</td>
                                    <td style={{ fontWeight: "bold" }}>{row.local_nombre || "—"}</td>
                                    <td>{renderStars(row.puntuacion ?? 0)}</td>
                                    <td style={{ maxWidth: 200, fontSize: 12, color: "#495057", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                      title={row.comentario || ""}>
                                      {row.comentario || <span className="text-muted">—</span>}
                                    </td>
                                    <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{row.fecha || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {/* Paginador */}
                          {totalPages > 1 && (
                            <Row className="mt-4 align-items-center">
                              <Col md="6" className="text-center text-md-left mb-2 mb-md-0">
                                <small className="text-muted">
                                  Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, reportePorComentarios.length)} de {reportePorComentarios.length} comentarios
                                </small>
                              </Col>
                              <Col md="6">
                                <div className="d-flex justify-content-center justify-content-md-end align-items-center">
                                  <nav aria-label="Paginación de reporte general">
                                    <ul className="pagination pagination-sm mb-0">
                                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                          className="page-link border-0"
                                          onClick={() => setCurrentPage(currentPage - 1)}
                                          disabled={currentPage === 1}
                                          style={{
                                            borderRadius: '8px',
                                            margin: '0 2px',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: currentPage === 1 ? '#6c757d' : '#495057',
                                            backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                                            border: '1px solid #e9ecef',
                                            minWidth: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <span>«</span>
                                        </button>
                                      </li>
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <li key={page} className="page-item">
                                          <button
                                            className="page-link border-0"
                                            onClick={() => setCurrentPage(page)}
                                            style={{
                                              borderRadius: '8px',
                                              margin: '0 2px',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              fontWeight: page === currentPage ? '600' : '500',
                                              color: page === currentPage ? 'white' : '#495057',
                                              backgroundColor: page === currentPage ? '#5A0C62' : 'white',
                                              border: page === currentPage ? '1px solid #5A0C62' : '1px solid #e9ecef',
                                              minWidth: '36px',
                                              height: '36px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              background: page === currentPage ? 'linear-gradient(135deg, #5A0C62 0%, #DC017F 100%)' : 'white'
                                            }}
                                          >
                                            {page}
                                          </button>
                                        </li>
                                      ))}
                                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                          className="page-link border-0"
                                          onClick={() => setCurrentPage(currentPage + 1)}
                                          disabled={currentPage === totalPages}
                                          style={{
                                            borderRadius: '8px',
                                            margin: '0 2px',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: currentPage === totalPages ? '#6c757d' : '#495057',
                                            backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                                            border: '1px solid #e9ecef',
                                            minWidth: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <span>»</span>
                                        </button>
                                      </li>
                                    </ul>
                                  </nav>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </>
                      );
                    })()}

                    {/* Lógica de paginación para Reporte por Preguntas */}
                    {(() => {
                      if (tabActiva !== "preguntas") return null;
                      if (reportePorPreguntas.length === 0) {
                        return (
                          <div className="text-center py-5 text-muted">
                            No hay datos disponibles para el reporte por preguntas.
                          </div>
                        );
                      }

                      // Cálculos de paginación
                      const indexOfLastItem = currentPage * itemsPerPage;
                      const indexOfFirstItem = indexOfLastItem - itemsPerPage;
                      const currentItems = reportePorPreguntas.slice(indexOfFirstItem, indexOfLastItem);
                      const totalPages = Math.ceil(reportePorPreguntas.length / itemsPerPage);

                      return (
                        <>
                          <div className="table-responsive">
                            <Table className="align-items-center table-flush" hover>
                              <thead className="thead-light">
                                <tr>
                                  <th>ID Evaluacion</th>
                                  <th>Tipo</th>
                                  <th>Turno</th>
                                  <th>ID Local</th>
                                  <th>Local</th>
                                  <th>ID Pregunta</th>
                                  <th>Pregunta</th>
                                  <th>Puntuación</th>
                                  <th>Fecha</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentItems.map((row, idx) => (
                                  <tr key={idx}>
                                    <td style={{ color: "#32325d", fontWeight: "bold", fontSize: 11 }}>{row.evaluacion}</td>
                                    <td>{row.tipo_local || "—"}</td>
                                    <td style={{ textAlign: "center" }}>{row.turno ?? "—"}</td>
                                    <td style={{ color: "#adb5bd", fontSize: 11 }}>{row.local_id}</td>
                                    <td style={{ fontWeight: "bold" }}>{row.local_nombre || "—"}</td>
                                    <td style={{ textAlign: "center", color: "#adb5bd" }}>{row.pregunta_id}</td>
                                    <td style={{ maxWidth: 220, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.pregunta}>{row.pregunta}</td>
                                    <td>{renderStars(row.puntuacion ?? 0)}</td>
                                    <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>{row.fecha || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {/* Paginador */}
                          {totalPages > 1 && (
                            <Row className="mt-4 align-items-center">
                              <Col md="6" className="text-center text-md-left mb-2 mb-md-0">
                                <small className="text-muted">
                                  Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, reportePorPreguntas.length)} de {reportePorPreguntas.length} respuestas
                                </small>
                              </Col>
                              <Col md="6">
                                <div className="d-flex justify-content-center justify-content-md-end align-items-center">
                                  <nav aria-label="Paginación de reporte por preguntas">
                                    <ul className="pagination pagination-sm mb-0">
                                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                          className="page-link border-0"
                                          onClick={() => setCurrentPage(currentPage - 1)}
                                          disabled={currentPage === 1}
                                          style={{
                                            borderRadius: '8px',
                                            margin: '0 2px',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: currentPage === 1 ? '#6c757d' : '#495057',
                                            backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                                            border: '1px solid #e9ecef',
                                            minWidth: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <span>«</span>
                                        </button>
                                      </li>
                                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <li key={page} className="page-item">
                                          <button
                                            className="page-link border-0"
                                            onClick={() => setCurrentPage(page)}
                                            style={{
                                              borderRadius: '8px',
                                              margin: '0 2px',
                                              padding: '8px 12px',
                                              fontSize: '14px',
                                              fontWeight: page === currentPage ? '600' : '500',
                                              color: page === currentPage ? 'white' : '#495057',
                                              backgroundColor: page === currentPage ? '#5A0C62' : 'white',
                                              border: page === currentPage ? '1px solid #5A0C62' : '1px solid #e9ecef',
                                              minWidth: '36px',
                                              height: '36px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              background: page === currentPage ? 'linear-gradient(135deg, #5A0C62 0%, #DC017F 100%)' : 'white'
                                            }}
                                          >
                                            {page}
                                          </button>
                                        </li>
                                      ))}
                                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                          className="page-link border-0"
                                          onClick={() => setCurrentPage(currentPage + 1)}
                                          disabled={currentPage === totalPages}
                                          style={{
                                            borderRadius: '8px',
                                            margin: '0 2px',
                                            padding: '8px 12px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            color: currentPage === totalPages ? '#6c757d' : '#495057',
                                            backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                                            border: '1px solid #e9ecef',
                                            minWidth: '36px',
                                            height: '36px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}
                                        >
                                          <span>»</span>
                                        </button>
                                      </li>
                                    </ul>
                                  </nav>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Vista inicial */}
                {!reporteGenerado && !loadingReporte && (
                  <div
                    className="text-center py-5 mt-3"
                    style={{
                      border: "2px dashed #dee2e6",
                      borderRadius: "16px",
                      background: "#fafafa"
                    }}
                  >
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h4>Configura los filtros y genera tu reporte</h4>
                    <p className="text-muted mb-0">
                      Selecciona un tipo de local y el rango de fechas para visualizar la información.
                    </p>
                  </div>
                )}

              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Reportes;